import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Archive, RotateCcw, Trash2, X, Loader2, ShieldAlert, CheckCircle2, History, Database, FlaskConical, Layers, CloudDownload, Lock } from "lucide-react";
import { getLifecycle, getHistory, getPreview, archiveKind, restoreKind, deleteKind, setTraining, LifecycleAlert } from "../../lib/lifecycleApi.jsx";
import { useRole } from "../../lib/role.jsx";
import { ClearCard, ClearPanel } from "./ClearPanel";
import { cn } from "../../lib/utils";

const ICON = { synthetic: FlaskConical, curated: Layers, actual: CloudDownload };
const ABOUT = {
  synthetic: "The platform's sample data: simulated sales, inventory, receipts and branch history.",
  curated: "What has been shaped from raw files: tables promoted to Silver and Gold facts.",
  actual: "Real 1Rotary data loaded from S3: ingest, Bronze-staged files and real Data Tables rows.",
};
const when = (iso) => (iso ? new Date(iso).toLocaleString("en-PH", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const num = (n) => (n ?? 0).toLocaleString("en-PH");
const btn = "inline-flex items-center justify-center gap-1.5 h-9 px-3.5 rounded-sm text-[12.5px] font-medium transition-[opacity,background-color,transform] active:scale-[0.98] disabled:opacity-45 disabled:cursor-not-allowed";

function StatusPill({ status }) {
  const cls = status === "active" ? "text-success border-success/50 bg-success/10" : status === "archived" ? "text-warning border-warning/50 bg-warning/10" : "text-critical border-critical/50 bg-critical/10";
  return <span data-testid="lc-status" className={cn("inline-flex items-center gap-1.5 h-6 px-2 rounded-sm border text-[10.5px] font-semibold uppercase tracking-wider", cls)}>
    <span className="w-1.5 h-1.5 rounded-full bg-current" />{status}</span>;
}

function Tables({ tables, limit = 6 }) {
  const [all, setAll] = useState(false);
  const shown = all ? tables : tables.slice(0, limit);
  return (
    <div className="text-[12px]">
      {shown.map(t => (
        <div key={t.name} className="flex justify-between gap-2 py-1 border-b border-border/50 last:border-b-0">
          <span className="truncate text-fg/85">{t.name}</span>
          <span className="font-mono-tabular shrink-0">{num(t.rows)}{t.unit ? ` ${t.rows === 1 ? t.unit.replace(/s$/, "") : t.unit}` : ""}</span>
        </div>
      ))}
      {tables.length > limit && <button data-testid="lc-tables-more" onClick={() => setAll(a => !a)} className="text-primary text-[11px] mt-1 hover:underline">{all ? "Show fewer" : `+${tables.length - limit} more tables`}</button>}
    </div>
  );
}

function KindCard({ c, canEdit, busy, onArchive, onRestore }) {
  const Icon = ICON[c.kind];
  const off = c.status !== "active";
  return (
    <div data-testid={`lc-card-${c.kind}`} className={cn("tile flex flex-col transition-opacity", off && "opacity-75")}>
      <div className="px-4 pt-4 pb-3 border-b border-border">
        <div className="flex items-center justify-between gap-2">
          <div className="inline-flex items-center gap-2 font-display font-semibold text-[15px]"><Icon size={16} className="text-primary" />{c.label}</div>
          <StatusPill status={c.status} />
        </div>
        <p className="text-[12px] text-muted mt-1.5">{ABOUT[c.kind]}</p>
      </div>
      <div className="px-4 py-3 space-y-3 flex-1">
        <div className="grid grid-cols-2 gap-3 text-[12px]">
          <div><div className="kpi-label">Holds</div><div className="kpi-value text-lg mt-0.5" data-testid={`lc-rows-${c.kind}`}>{(off ? c.archived_summary : c.summary) || "—"}</div></div>
          <div><div className="kpi-label">Date range</div><div className="font-mono-tabular mt-1" data-testid={`lc-range-${c.kind}`}>{c.date_range ? `${c.date_range[0]} → ${c.date_range[1]}` : "—"}</div></div>
        </div>
        <Tables tables={c.tables} />
        <div>
          <div className="kpi-label mb-1">Used by</div>
          <div className="flex flex-wrap gap-1" data-testid={`lc-models-${c.kind}`}>
            {off ? <span className="text-[11.5px] text-muted">Nothing — archived</span>
              : c.models.length ? c.models.map(m => <span key={m} className="text-[11px] px-1.5 py-0.5 rounded-sm bg-surface-2 border border-border">{m}</span>)
              : <span className="text-[11.5px] text-muted">No model uses it yet</span>}
          </div>
        </div>
        <div className="text-[11px] text-muted font-mono-tabular" data-testid={`lc-changed-${c.kind}`}>
          {c.changed_at ? `Last change ${when(c.changed_at)} · ${c.changed_by}` : "Active since setup"}
        </div>
      </div>
      <div className="px-4 py-3 border-t border-border space-y-2">
        {c.status === "active" ? (
          <>
            <button data-testid={`lc-archive-${c.kind}`} disabled={!canEdit || !!c.archive_blocked || busy} onClick={() => onArchive(c.kind)}
                    className={cn(btn, "w-full border border-critical/60 text-critical hover:bg-critical/10")}><Archive size={13} />Archive {c.label}</button>
            {c.archive_blocked && <div className="text-[11px] text-muted inline-flex gap-1.5" data-testid={`lc-blocked-${c.kind}`}><Lock size={11} className="mt-0.5 shrink-0" />{c.archive_blocked}</div>}
          </>
        ) : (
          <button data-testid={c.kind === "synthetic" ? "lc-restore-synthetic" : `lc-restore-${c.kind}`} disabled={!canEdit || busy} onClick={() => onRestore(c.kind)}
                  className={cn(btn, "w-full bg-primary text-primary-fg hover:opacity-90")}><RotateCcw size={13} />{c.kind === "synthetic" ? "Restore Synthetic" : `Restore ${c.label}`}</button>
        )}
        {!canEdit && <div className="text-[11px] text-muted">View only — the IT Head manages data lifecycle.</div>}
      </div>
    </div>
  );
}

function Panel({ children, onClose, testid }) {
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[60]" data-testid={testid}>
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full sm:w-[520px] bg-bg border-l border-border shadow-2xl overflow-y-auto fc-slide-in">{children}</aside>
    </div>, document.body);
}

function ArchivePanel({ kind, onClose, onDone }) {
  const [p, setP] = useState(null);
  const [word, setWord] = useState("");
  const [reason, setReason] = useState("");
  const [state, setState] = useState({ busy: false, error: null });
  useEffect(() => { getPreview(kind).then(setP).catch(e => setState({ busy: false, error: e.message })); }, [kind]);
  const ok = p && word.trim().toLowerCase() === p.confirm_word.toLowerCase();
  const go = async () => {
    setState({ busy: true, error: null });
    try { await archiveKind(kind, word, reason); await onDone(); } catch (e) { setState({ busy: false, error: e.message }); }
  };
  return (
    <Panel onClose={onClose} testid="lc-archive-panel">
      <div className="sticky top-0 bg-bg/95 backdrop-blur-md border-b border-border px-5 py-4 flex items-start justify-between gap-3 z-10">
        <div><div className="text-[10px] uppercase tracking-[0.18em] text-critical font-semibold">Purge to archive</div>
          <div className="font-display text-lg font-semibold">Archive {p ? p.label : "…"}</div></div>
        <button data-testid="lc-archive-close" onClick={onClose} aria-label="Close" className="h-8 w-8 inline-flex items-center justify-center border border-border rounded-sm hover:border-primary"><X size={15} /></button>
      </div>
      {!p ? <div className="p-5 text-sm text-muted">{state.error || "Loading preview…"}</div> : (
        <div className="px-5 py-5 space-y-6 text-sm">
          <p className="text-[13px] text-fg/85">Nothing is deleted. The whole {p.label.toLowerCase()} data type stops being used on the next run, which starts straight after you confirm, and it can be restored at any time.</p>
          <section><div className="kpi-label mb-1.5">Tables and rows · {p.summary}</div><Tables tables={p.tables} limit={20} /></section>
          <section><div className="kpi-label mb-1.5">Models affected</div>
            <div className="flex flex-wrap gap-1" data-testid="lc-preview-models">{p.models.length ? p.models.map(m => <span key={m} className="text-[11px] px-1.5 py-0.5 rounded-sm bg-surface-2 border border-border">{m}</span>) : <span className="text-muted text-[12px]">None</span>}</div></section>
          <section><div className="kpi-label mb-1.5">What each screen falls back to</div>
            <table className="w-full text-[12px]" data-testid="lc-preview-fallback"><thead><tr className="text-[10px] uppercase tracking-widest text-muted">
              <th className="text-left py-1.5">Screen</th><th className="text-left py-1.5">Now</th><th className="text-left py-1.5">After archive</th></tr></thead>
              <tbody>{p.fallback.map(f => <tr key={f.screen} className="border-t border-border/60 align-top"><td className="py-1.5 pr-2 font-medium">{f.screen}</td><td className="py-1.5 pr-2 text-muted">{f.now}</td><td className="py-1.5">{f.after}</td></tr>)}</tbody></table></section>
          <section className="space-y-2">
            <label className="block text-[12px]">Reason (optional)
              <input data-testid="lc-archive-reason" value={reason} onChange={e => setReason(e.target.value)} maxLength={300} className="mt-1 w-full h-9 bg-surface border border-border rounded-sm px-2.5 focus:outline-none focus:border-primary" /></label>
            <label className="block text-[12px]">Type <span className="font-mono-tabular font-semibold text-critical">{p.confirm_word}</span> to confirm
              <input data-testid="lc-archive-confirm" value={word} onChange={e => setWord(e.target.value)} className="mt-1 w-full h-9 bg-surface border border-border rounded-sm px-2.5 focus:outline-none focus:border-critical" /></label>
            {state.error && <div className="text-critical text-[12px]" data-testid="lc-archive-error">{state.error}</div>}
            <button data-testid="lc-archive-submit" disabled={!ok || state.busy || !!p.archive_blocked} onClick={go} className={cn(btn, "w-full bg-critical text-white hover:opacity-90")}>
              {state.busy ? <Loader2 size={13} className="animate-spin" /> : <Archive size={13} />}Archive {p.label}</button>
          </section>
        </div>
      )}
    </Panel>
  );
}

function DeletePanel({ card, onClose, onDone }) {
  const phrase = `delete ${card.label.toLowerCase()} for good`;
  const [word, setWord] = useState("");
  const [reason, setReason] = useState("");
  const [state, setState] = useState({ busy: false, error: null });
  const go = async () => {
    setState({ busy: true, error: null });
    try { await deleteKind(card.kind, word, reason); await onDone(); } catch (e) { setState({ busy: false, error: e.message }); }
  };
  return (
    <Panel onClose={onClose} testid="lc-delete-panel">
      <div className="px-5 py-5 space-y-4 text-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="font-display text-lg font-semibold text-critical inline-flex items-center gap-2"><ShieldAlert size={18} />Delete {card.label} for good</div>
          <button data-testid="lc-delete-close" onClick={onClose} aria-label="Close" className="h-8 w-8 inline-flex items-center justify-center border border-border rounded-sm"><X size={15} /></button>
        </div>
        <p className="text-[13px]">This permanently removes the archived {card.label.toLowerCase()} data ({card.archived_summary || `${num(card.archived_rows)} rows`}) from the platform. It can't be undone.
          {card.kind === "synthetic" && " Restore Synthetic can still regenerate the standard sample dataset later."}
          {card.kind === "actual" && " Files in 1Rotary's S3 bucket are never touched."}
          {card.kind === "curated" && " Gold facts are removed and Silver promotions go back to Bronze-staged; restoring later re-enables curation but doesn't bring them back."}</p>
        <label className="block text-[12px]">Reason (optional)
          <input data-testid="lc-delete-reason" value={reason} onChange={e => setReason(e.target.value)} maxLength={300} className="mt-1 w-full h-9 bg-surface border border-border rounded-sm px-2.5" /></label>
        <label className="block text-[12px]">Type <span className="font-mono-tabular font-semibold text-critical">{phrase}</span> to confirm
          <input data-testid="lc-delete-confirm" value={word} onChange={e => setWord(e.target.value)} className="mt-1 w-full h-9 bg-surface border border-border rounded-sm px-2.5 focus:outline-none focus:border-critical" /></label>
        {state.error && <div className="text-critical text-[12px]" data-testid="lc-delete-error">{state.error}</div>}
        <button data-testid="lc-delete-submit" disabled={word.trim().toLowerCase() !== phrase || state.busy} onClick={go} className={cn(btn, "w-full bg-critical text-white hover:opacity-90")}>
          {state.busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}Delete for good</button>
      </div>
    </Panel>
  );
}

function ArchiveList({ cards, canEdit, busy, onRestore, onDelete }) {
  const off = cards.filter(c => c.status !== "active");
  return (
    <div className="tile" data-testid="lc-archive-list">
      <div className="px-4 py-3 border-b border-border kpi-label inline-flex items-center gap-1.5 w-full"><Archive size={12} />Archive</div>
      {!off.length ? <div className="px-4 py-5 text-sm text-muted" data-testid="lc-archive-empty">Nothing is archived. Every data type is in use.</div> : (
        <div className="divide-y divide-border">
          {off.map(c => (
            <div key={c.kind} className="px-4 py-3 flex flex-wrap items-center gap-3" data-testid={`lc-archived-${c.kind}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium flex items-center gap-2">{c.label}<StatusPill status={c.status} /></div>
                <div className="text-[11.5px] text-muted font-mono-tabular">{c.status === "deleted" ? "Deleted" : "Archived"} {when(c.changed_at)} · {c.changed_by} · {c.archived_summary || `${num(c.archived_rows)} rows`}</div>
              </div>
              <button data-testid={`lc-list-restore-${c.kind}`} disabled={!canEdit || busy} onClick={() => onRestore(c.kind)} className={cn(btn, "border border-primary/60 text-primary hover:bg-primary/10")}><RotateCcw size={13} />{c.kind === "synthetic" ? "Restore Synthetic" : "Restore"}</button>
              {c.status === "archived" && <button data-testid={`lc-list-delete-${c.kind}`} disabled={!canEdit || busy} onClick={() => onDelete(c)} className={cn(btn, "border border-critical/60 text-critical hover:bg-critical/10")}><Trash2 size={13} />Delete for good</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ACTION = { archive: "Archived", restore: "Restored", restore_synthetic: "Restore Synthetic", delete: "Deleted for good", training_choice: "Training choice", upload: "Upload to Bronze", mapping_change: "Mapping change",
  cleanup_rule: "Clean-up rule", promote_silver: "Promote to Silver", promote_gold: "Certify Gold", demote: "Back to Silver",
  switch_on: "Use uploaded data · on", switch_off: "Use uploaded data · off", reject: "Bronze item rejected", clear_bronze_silver: "Clear Bronze & Silver",
  row_fix: "Row fix", ai_fix: "AI suggested fix", save_steps: "Saved cleaning steps", delete_steps: "Saved steps removed" };

function HistoryLog({ events }) {
  return (
    <div className="tile" data-testid="lc-history">
      <div className="px-4 py-3 border-b border-border kpi-label inline-flex items-center gap-1.5 w-full"><History size={12} />History</div>
      {!events.length ? <div className="px-4 py-5 text-sm text-muted">No lifecycle changes yet.</div> : (
        <div className="overflow-x-auto"><table className="w-full text-[12px]">
          <thead><tr className="text-[10px] uppercase tracking-widest text-muted">{["When", "Action", "Data type", "Rows", "By", "Reason / detail"].map(h => <th key={h} className="text-left px-4 py-2 border-b border-border whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody>{events.map(e => (
            <tr key={e.id} className="border-b border-border/60 last:border-b-0" data-testid={`lc-event-${e.id}`}>
              <td className="px-4 py-2 font-mono-tabular whitespace-nowrap">{when(e.at)}</td><td className="px-4 py-2 whitespace-nowrap">{ACTION[e.action] || e.action}</td>
              <td className="px-4 py-2 capitalize">{e.kind || "—"}</td><td className="px-4 py-2 font-mono-tabular">{e.summary || (e.rows == null ? "—" : num(e.rows))}</td>
              <td className="px-4 py-2 font-mono-tabular">{e.actor}</td><td className="px-4 py-2 text-muted">{[e.reason, e.detail].filter(Boolean).join(" · ") || "—"}</td>
            </tr>))}</tbody></table></div>
      )}
    </div>
  );
}

function TrainingChoice({ onClose }) {
  const [state, setState] = useState({ busy: null, error: null });
  const pick = async (use) => {
    setState({ busy: use, error: null });
    try { await setTraining(use); onClose(); } catch (e) { setState({ busy: null, error: e.message }); }
  };
  return (
    <Panel onClose={onClose} testid="lc-training">
      <div className="px-5 py-5 space-y-4 text-sm">
        <div className="font-display text-lg font-semibold">Which data should the models use?</div>
        <p className="text-[13px] text-muted">Synthetic is back and actual data is also active. Pick what the ABC × XYZ classification uses; the Actual / Synthetic labels show the result.</p>
        {state.error && <div className="text-critical text-[12px]" data-testid="lc-training-error">{state.error}</div>}
        <div className="grid gap-2">
          <button data-testid="lc-training-synthetic" disabled={!!state.busy} onClick={() => pick("synthetic")} className={cn(btn, "bg-primary text-primary-fg")}>{state.busy === "synthetic" && <Loader2 size={13} className="animate-spin" />}Use synthetic for training</button>
          <button data-testid="lc-training-actual" disabled={!!state.busy} onClick={() => pick("actual")} className={cn(btn, "border border-border hover:border-primary")}>{state.busy === "actual" && <Loader2 size={13} className="animate-spin" />}Keep actual</button>
        </div>
      </div>
    </Panel>
  );
}

function JobBar({ job }) {
  if (!job?.action) return null;
  return (
    <div data-testid="lc-job" className="tile px-4 py-2.5 flex items-center gap-2 text-[12.5px]">
      {job.running ? <Loader2 size={14} className="animate-spin text-primary" /> : job.error ? <ShieldAlert size={14} className="text-critical" /> : <CheckCircle2 size={14} className="text-success" />}
      <span className="font-medium capitalize">{job.action}</span>
      <span className="text-muted" data-testid="lc-job-step">{job.running ? `${job.step}…` : job.error ? `Models rerun failed: ${job.error}` : `Models reran · ${when(job.finished_at)}`}</span>
    </div>
  );
}

export default function DataLifecycleTab() {
  const { role } = useRole();
  const [d, setD] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [training, setTrainingOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => Promise.all([getLifecycle(), getHistory()]).then(([l, h]) => { setD(l); setEvents(h.events); setError(null); }).catch(e => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!d?.job?.running) return undefined;
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [d?.job?.running, load]);
  const restore = async (kind) => {
    setBusy(true);
    try { const r = await restoreKind(kind, ""); if (r.needs_training_choice) setTrainingOpen(true); await load(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  if (!d) return <div className="tile p-6 text-sm text-muted" data-testid="lc-loading">{error || "Loading data lifecycle…"}</div>;
  const canEdit = d.can_edit && role?.id === "it_head";
  return (
    <div className="space-y-6" data-testid="lc-tab">
      <LifecycleAlert alert={d.alert} isItHead={canEdit} onRestored={load} />
      <div className="flex flex-wrap items-center gap-3 text-[12px] text-muted">
        <Database size={13} /><span>Models currently use <span className="text-fg font-medium" data-testid="lc-models-use">{d.models_use === "actual" ? "actual 1Rotary data" : "synthetic sample data"}</span> for the ABC × XYZ classification.</span>
      </div>
      {error && <div className="text-critical text-[12px]" data-testid="lc-error">{error}</div>}
      <JobBar job={d.job} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {d.cards.map(c => <KindCard key={c.kind} c={c} canEdit={canEdit} busy={busy || d.job.running} onArchive={setArchiving} onRestore={restore} />)}
      </div>
      <ClearCard canEdit={canEdit} busy={busy || d.job.running} onOpen={() => setClearing(true)} />
      <ArchiveList cards={d.cards} canEdit={canEdit} busy={busy || d.job.running} onRestore={restore} onDelete={setDeleting} />
      <HistoryLog events={events} />
      {archiving && <ArchivePanel kind={archiving} onClose={() => setArchiving(null)} onDone={async () => { await load(); setArchiving(null); }} />}
      {deleting && <DeletePanel card={deleting} onClose={() => setDeleting(null)} onDone={async () => { await load(); setDeleting(null); }} />}
      {clearing && <ClearPanel onClose={() => setClearing(false)} onDone={async () => { await load(); setClearing(false); }} />}
      {training && <TrainingChoice onClose={() => { setTrainingOpen(false); load(); }} />}
    </div>
  );
}
